import React, { useRef, useEffect, useCallback } from "react";

const CHIP_CLASS = 'ph-chip';

const chipInlineStyle = [
    'display:inline-block',
    'padding:1px 8px',
    'border-radius:10px',
    'background:#EEF2FF',
    'color:#4F46E5',
    'border:1px solid #C7D2FE',
    'font-size:11px',
    'font-weight:500',
    'user-select:none',
    'cursor:default',
    'margin:0 2px',
    'vertical-align:middle',
    'line-height:1.8',
].join(';');

const chipButtonStyle = {
    cursor: 'pointer',
    fontSize: '11px',
    padding: '2px 8px',
    borderRadius: '12px',
    backgroundColor: '#EEF2FF',
    color: '#4F46E5',
    border: '1px solid #C7D2FE',
    userSelect: 'none',
};

function escHtml(s) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Convert plain-text value (with {placeholder}) to HTML with chip spans
function valueToHtml(text) {
    if (!text) return '';
    return text
        .split(/(\{[^{}]+\})/)
        .map(part =>
            /^\{[^{}]+\}$/.test(part)
                ? `<span class="${CHIP_CLASS}" contenteditable="false" style="${chipInlineStyle}">${escHtml(part)}</span>`
                : escHtml(part).replace(/\n/g, '<br>')
        )
        .join('');
}

// Extract plain text from the contentEditable el (chip spans → their textContent)
function extractValue(el) {
    let result = '';
    const walk = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            result += node.textContent;
        } else if (node.nodeName === 'BR') {
            result += '\n';
        } else if (node.nodeName === 'DIV' || node.nodeName === 'P') {
            // Chrome wraps new lines in divs/p
            if (result && !result.endsWith('\n')) result += '\n';
            for (const c of node.childNodes) walk(c);
        } else {
            for (const c of node.childNodes) walk(c);
        }
    };
    walk(el);
    return result;
}

function ChipEditor({ value, onChange, placeholders, placeholder, multiline, style, onKeyDown: externalKeyDown }) {
    const ref = useRef(null);
    const isFocused = useRef(false);
    const lastValue = useRef(value ?? '');

    const render = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        el.innerHTML = valueToHtml(lastValue.current);
    }, []);

    // Initial render
    useEffect(() => {
        lastValue.current = value ?? '';
        render();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Sync external value changes (e.g. Format JSON) when not focused
    useEffect(() => {
        if (value === lastValue.current) return;
        lastValue.current = value ?? '';
        if (!isFocused.current) render();
    }, [value, render]);

    const handleInput = () => {
        const el = ref.current;
        if (!el) return;
        const text = extractValue(el);
        lastValue.current = text;
        onChange(text);
    };

    const handleBlur = () => {
        isFocused.current = false;
        // Re-render: converts any manually-typed {placeholder} text to chips
        render();
    };

    const handleFocus = () => {
        isFocused.current = true;
    };

    const handleKeyDown = (e) => {
        if (!multiline && e.key === 'Enter') e.preventDefault();
        if (externalKeyDown) externalKeyDown(e);
    };

    // Insert chip at current cursor position.
    // onMouseDown on chip buttons uses e.preventDefault() so focus never leaves
    // the editor — the cursor position is preserved and el.focus() is not needed.
    const insert = (tag) => {
        const el = ref.current;
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;

        const range = sel.getRangeAt(0);
        range.deleteContents();

        const chip = document.createElement('span');
        chip.className = CHIP_CLASS;
        chip.contentEditable = 'false';
        chip.textContent = tag;
        chip.style.cssText = chipInlineStyle;

        range.insertNode(chip);

        // Move cursor right after the chip
        const after = document.createRange();
        after.setStartAfter(chip);
        after.collapse(true);
        sel.removeAllRanges();
        sel.addRange(after);

        const text = extractValue(el);
        lastValue.current = text;
        onChange(text);
    };

    const isEmpty = !value || value.trim() === '';

    const editorStyle = {
        width: '100%',
        padding: '8px 10px',
        borderRadius: '6px',
        border: '1px solid #E2E8F0',
        fontSize: '13px',
        outline: 'none',
        color: '#334155',
        boxSizing: 'border-box',
        lineHeight: '1.7',
        wordBreak: 'break-all',
        position: 'relative',
        ...style,
        ...(multiline
            ? { minHeight: style?.minHeight || '80px', resize: 'vertical', overflowY: 'auto' }
            : { minHeight: '36px', whiteSpace: 'nowrap', overflowX: 'auto', overflowY: 'hidden' }
        ),
    };

    return (
        <div style={{ position: 'relative' }}>
            {isEmpty && placeholder && (
                <div style={{
                    position: 'absolute', top: '8px', left: '10px',
                    fontSize: '13px', color: '#94A3B8', pointerEvents: 'none',
                    userSelect: 'none', zIndex: 1,
                }}>
                    {placeholder}
                </div>
            )}
            <div
                ref={ref}
                contentEditable
                suppressContentEditableWarning
                onInput={handleInput}
                onBlur={handleBlur}
                onFocus={handleFocus}
                onKeyDown={handleKeyDown}
                style={editorStyle}
            />
            {placeholders && placeholders.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                    {placeholders.map(p => (
                        <span
                            key={p}
                            onMouseDown={e => e.preventDefault()} // keeps focus + cursor in editor
                            onClick={() => insert(p)}
                            style={chipButtonStyle}
                        >{p}</span>
                    ))}
                </div>
            )}
        </div>
    );
}

export function PlaceholderTextarea({ value, onChange, placeholders, placeholder, style, ...rest }) {
    return (
        <ChipEditor
            value={value}
            onChange={onChange}
            placeholders={placeholders}
            placeholder={placeholder}
            multiline
            style={style}
            {...rest}
        />
    );
}

export function PlaceholderInput({ value, onChange, placeholders, placeholder, style, ...rest }) {
    return (
        <ChipEditor
            value={value}
            onChange={onChange}
            placeholders={placeholders}
            placeholder={placeholder}
            multiline={false}
            style={style}
            {...rest}
        />
    );
}
